import { useDomainTable } from '../../../hooks';

import { TablePanel } from '../panels';

import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  TablePagination,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { Warning } from '@mui/icons-material';

const TABLES_HEAD_BODY = {
  namecheap: {
    head: ['ID', 'Домен', 'Создан', 'Истекает', 'Заблокирован', 'WhoisGuard'],
    body: (row) => [row?.$.ID, row?.$.Name, row?.$.Created, row?.$.Expires, row?.$.IsLocked, row?.$.WhoisGuard],
  },
  netcup: {
    head: ['ID', 'Домен', 'Создан', 'Истекает', 'Заблокирован', 'WhoisGuard'],
    body: (row) => ['row', 'row', 'row', 'row', 'row', 'row'],
  },
  inwx: {
    head: ['Домен', 'Создан', 'Истекает', 'Статус', 'Auto renew'],
    body: (row) => [row.domain, row.crDate.scalar, row.exDate.scalar, row.status, row.renewalMode],
  },
  default: {
    head: ['Нет данных'],
    body: () => ['Нет данных'],
  },
};

const SORT_MAPPING = {
  inwx: {
    Домен: 'DOMAIN',
    Создан: 'CRDATE',
    Истекает: 'EXDATE',
    Статус: 'STATUS',
    'Auto renew': 'RENEWALMODE',
  },
  namecheap: {
    Домен: 'NAME',
    Создан: 'CREATEDATE',
    Истекает: 'EXPIREDATE',
  },
};

const getNextSortOrder = ({ serverName, current, base }) => {
  if (serverName === 'inwx') {
    return current === `${base}ASC` ? `${base}DESC` : `${base}ASC`;
  } else if (serverName === 'namecheap') {
    return current === base ? `${base}_DESC` : base;
  }
};

const isExpiringSoon = (exDateStr) => {
  const exDate = new Date(exDateStr);
  const now = new Date();
  const diffInMs = exDate - now;
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
  return diffInDays < 7;
};

const shouldHighlightRow = (row, serverName) => {
  switch (serverName) {
    case 'inwx':
      return isExpiringSoon(row?.exDate?.scalar);
    case 'namecheap':
      return isExpiringSoon(row?.$?.Expires);
    default:
      return false;
  }
};

const isBlocked = (row, serverName) => {
  switch (serverName) {
    case 'inwx':
      return row?.status !== 'OK';
    case 'namecheap':
      return row?.$?.IsLocked !== 'false';
    default:
      return false;
  }
};

export const DomainsTable = ({ userName, serverName = 'default' }) => {
  const {
    totalItems,
    totalPages,
    domains,
    paging,
    balance,
    balanceCurrency,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearch,
    isLoading,
    sortOrder,
    setSortOrder,
    refreshTable,
  } = useDomainTable({ userName, serverName });

  const tableConfig = TABLES_HEAD_BODY[serverName] || TABLES_HEAD_BODY.default;

  const handleHeaderClick = (header) => {
    const sortConfig = SORT_MAPPING[serverName]?.[header];
    if (!sortConfig) {
      return;
    }

    const newSort = getNextSortOrder({ serverName, current: sortOrder, base: sortConfig });
    setSortOrder(newSort);
  };

  return (
    <>
      <TablePanel
        userName={userName}
        serverName={serverName}
        useHook={useDomainTable}
        handleSearch={handleSearch}
        balance={balance}
        balanceCurrency={balanceCurrency}
        isLoading={isLoading}
        refreshTable={refreshTable}
      />

      <TableContainer
        component={Paper}
        sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto', borderRadius: 2, boxShadow: 3 }}
      >
        <Table stickyHeader sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              {tableConfig.head.map((head) => (
                <TableCell
                  key={head}
                  onClick={() => handleHeaderClick(head)}
                  style={{ cursor: SORT_MAPPING[serverName] && SORT_MAPPING[serverName][head] ? 'pointer' : 'default' }}
                >
                  {head}{' '}
                  {sortOrder && sortOrder.startsWith(SORT_MAPPING[serverName]?.[head])
                    ? !sortOrder.endsWith('DESC')
                      ? '▲'
                      : '▼'
                    : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`loading-${index}`}>
                  {tableConfig.head.map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton animation='wave' />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : domains && domains.length > 0 ? (
              domains.map((row, rowIndex) => {
                const highlightRow = shouldHighlightRow(row, serverName);
                const blockedRow = isBlocked(row, serverName);

                return (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      backgroundColor: blockedRow
                        ? 'rgba(255, 0, 0, 0.5)'
                        : highlightRow
                        ? 'rgba(255, 255, 0, 0.3)'
                        : 'inherit',
                    }}
                  >
                    {tableConfig.body(row).map((cell, cellIndex) => (
                      <TableCell key={cellIndex}>{typeof cell === 'object' ? JSON.stringify(cell) : cell}</TableCell>
                    ))}
                    {highlightRow && (
                      <TableCell>
                        <Tooltip title='Домен скоро истекает' arrow>
                          <Warning sx={{ color: '#ffa726' }} />
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={tableConfig.head.length} align='center'>
                  {isLoading ? 'Загрузка...' : 'Нет данных'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box mt={2} display='flex' justifyContent='center'>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100]}
            component='div'
            count={totalItems}
            rowsPerPage={+paging.PageSize}
            page={Math.max(0, +paging.CurrentPage - 1)}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              width: '100%',
              backgroundColor: '#0000004a',
              borderRadius: 1,
              p: 1,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
            }}
          />
        </Box>
      )}
    </>
  );
};
